import { JobillanBadge } from '@/ui/core/jobillan-badge'
import {
	ChatBubbleLeftRightIcon,
	ExclamationTriangleIcon,
	PaperAirplaneIcon,
	UserGroupIcon,
} from '@heroicons/react/24/outline'
import type { AnalyticsWhatsAppOrganisationAnalyticsResponse } from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'

export type WhatsAppAnalyticsSectionProps = {
	data: AnalyticsWhatsAppOrganisationAnalyticsResponse | undefined
	isLoading: boolean
	isError: boolean
}

export function WhatsAppAnalyticsSection({ data, isLoading, isError }: WhatsAppAnalyticsSectionProps) {
	return (
		<div>
			<div className="flex items-baseline justify-between mb-4">
				<h2 className="text-xs uppercase tracking-widest font-semibold text-grey-500 flex items-center gap-2">
					WhatsApp
					<JobillanBadge />
				</h2>
				{data && (
					<span className="text-xs text-grey-400">
						{formatPeriod(data.period)}
					</span>
				)}
			</div>

			{isLoading
				? (
					<div className="grid grid-cols-3 gap-4">
						{[0, 1, 2].map((i) => <MetricSkeleton key={i} delay={i * 0.1} />)}
					</div>
				)
				: isError || !data
				? (
					<div className="text-sm text-grey-500 text-center py-12 bg-white border border-grey-200 rounded-lg">
						Failed to load WhatsApp analytics data.
					</div>
				)
				: (
					<div className="space-y-6">
						<div>
							<h3 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
								Delivery
							</h3>
							<div className="grid grid-cols-3 gap-4">
								<MetricCard
									label="Messages Sent"
									value={data.totalOutbound}
									icon={<PaperAirplaneIcon className="size-5" />}
									color="emerald"
									delay={0}
								/>
								<MetricCard
									label="Delivery Rate"
									value={formatPercent(data.deliveryRate)}
									icon={<ChatBubbleLeftRightIcon className="size-5" />}
									color="sky"
									delay={0.1}
								/>
								<MetricCard
									label="Failed"
									value={data.failedCount}
									icon={<ExclamationTriangleIcon className="size-5" />}
									color={data.failedCount > 0 ? 'orange' : 'slate'}
									delay={0.2}
								/>
							</div>
						</div>

						<div>
							<h3 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
								Engagement
							</h3>
							<div className="grid grid-cols-3 gap-4">
								<MetricCard
									label="Candidates Contacted"
									value={data.candidatesContacted}
									icon={<UserGroupIcon className="size-5" />}
									color="purple"
									delay={0.1}
								/>
								<MetricCard
									label="Replied"
									value={`${data.candidatesReplied} (${formatPercent(data.replyRate)})`}
									icon={<ChatBubbleLeftRightIcon className="size-5" />}
									color="teal"
									delay={0.2}
								/>
								<MetricCard
									label="Read Rate"
									value={formatPercent(data.readRate)}
									icon={<ChatBubbleLeftRightIcon className="size-5" />}
									color="indigo"
									delay={0.3}
								/>
							</div>
						</div>

						{data.templateBreakdown.length > 0 && (
							<div>
								<h3 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
									Template Performance
								</h3>
								<div className="border border-grey-200 rounded-lg overflow-hidden bg-white">
									<table className="w-full text-sm">
										<thead>
											<tr className="bg-grey-50 text-grey-600">
												<th className="text-left px-4 py-2.5 font-medium">Template</th>
												<th className="text-right px-4 py-2.5 font-medium">Sent</th>
												<th className="text-right px-4 py-2.5 font-medium">Delivered</th>
												<th className="text-right px-4 py-2.5 font-medium">Read</th>
												<th className="text-right px-4 py-2.5 font-medium">Failed</th>
											</tr>
										</thead>
										<tbody>
											{data.templateBreakdown.map((template, index) => (
												<tr
													key={template.templateId ?? `unknown-${index}`}
													className="border-t border-grey-100"
												>
													<td className="px-4 py-2.5 text-grey-900">
														{template.templateName ?? 'Unknown'}
													</td>
													<td className="text-right px-4 py-2.5 text-grey-700">
														{template.sentCount}
													</td>
													<td className="text-right px-4 py-2.5 text-grey-700">
														{template.deliveredCount}
													</td>
													<td className="text-right px-4 py-2.5 text-grey-700">
														{template.readCount}
													</td>
													<td className="text-right px-4 py-2.5 text-grey-700">
														{template.failedCount > 0
															? (
																<span className="text-orange-600">
																	{template.failedCount}
																</span>
															)
															: template.failedCount}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						)}

						{data.totalOutbound === 0 && (
							<div className="text-sm text-grey-500 text-center py-8 bg-grey-50 rounded-lg">
								No WhatsApp messages found for this period.
							</div>
						)}
					</div>
				)}
		</div>
	)
}

function MetricCard({
	label,
	value,
	icon,
	color,
	delay = 0,
}: {
	label: string
	value: string | number
	icon: React.ReactNode
	color: 'emerald' | 'sky' | 'orange' | 'slate' | 'purple' | 'teal' | 'indigo'
	delay?: number
}) {
	const styles = {
		emerald: {
			icon: 'bg-emerald-500 text-white',
			border: 'border-emerald-200',
			bg: 'bg-emerald-50/50',
			value: 'text-emerald-700',
		},
		sky: { icon: 'bg-sky-500 text-white', border: 'border-sky-200', bg: 'bg-sky-50/50', value: 'text-sky-700' },
		orange: {
			icon: 'bg-orange-500 text-white',
			border: 'border-orange-200',
			bg: 'bg-orange-50/50',
			value: 'text-orange-700',
		},
		slate: {
			icon: 'bg-slate-400 text-white',
			border: 'border-slate-200',
			bg: 'bg-slate-50/50',
			value: 'text-slate-600',
		},
		purple: {
			icon: 'bg-purple-500 text-white',
			border: 'border-purple-200',
			bg: 'bg-purple-50/50',
			value: 'text-purple-700',
		},
		teal: {
			icon: 'bg-teal-500 text-white',
			border: 'border-teal-200',
			bg: 'bg-teal-50/50',
			value: 'text-teal-700',
		},
		indigo: {
			icon: 'bg-indigo-500 text-white',
			border: 'border-indigo-200',
			bg: 'bg-indigo-50/50',
			value: 'text-indigo-700',
		},
	}

	const s = styles[color]

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className={`p-5 rounded-xl border-2 ${s.border} ${s.bg}`}
		>
			<div className="flex items-start justify-between mb-3">
				<div className="text-xs font-semibold text-grey-600 uppercase tracking-wide">{label}</div>
				<div className={`p-2 rounded-lg ${s.icon}`}>{icon}</div>
			</div>
			<div className={`text-2xl font-bold ${s.value}`}>{value}</div>
		</motion.div>
	)
}

function MetricSkeleton({ delay = 0 }: { delay?: number }) {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className="p-5 rounded-xl border-2 border-slate-200 bg-slate-50/40"
		>
			<div className="flex items-start justify-between mb-3">
				<div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
				<div className="h-8 w-8 bg-slate-200 rounded-lg animate-pulse" />
			</div>
			<div className="h-7 w-16 bg-slate-200 rounded animate-pulse" />
		</motion.div>
	)
}

function formatPercent(rate: number): string {
	return `${(rate * 100).toFixed(1)}%`
}

function formatPeriod(period: { startDate: string; endDate: string }): string {
	const format = (date: string) => {
		// Construct from the calendar parts so a date-only value (YYYY-MM-DD) is not
		// parsed as UTC midnight and shifted a day for users in negative-offset zones.
		const [year, month, day] = date.split('-').map(Number)
		return new Date(year, month - 1, day).toLocaleDateString(undefined, {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		})
	}
	return `${format(period.startDate)} – ${format(period.endDate)}`
}
